import { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StarIcon from '@mui/icons-material/Star';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import StorageIcon from '@mui/icons-material/Storage';
import AuthContext from '../../context/authContext';
import useClusterHealth from '../../hooks/useClusterHealth';

const AUTO_REFRESH_INTERVAL = 10000;

const THRESHOLDS = {
  eventLoopP99WarnMs: 100,
  eventLoopP99ErrorMs: 500,
  mongoMeanWarnMs: 50,
  mongoMeanErrorMs: 200,
  mongoQueryP99WarnMs: 100,
  mongoQueryP99ErrorMs: 500,
  redisMeanWarnMs: 20,
  redisMeanErrorMs: 100,
  totalPendingWarn: 100,
  totalPendingError: 1000,
};

const colorForLatency = (value, warn, error) => {
  if (value == null) return 'default';
  if (value >= error) return 'error';
  if (value >= warn) return 'warning';
  return 'success';
};

const colorForCount = (value, warn, error) => {
  if (value >= error) return 'error';
  if (value >= warn) return 'warning';
  return 'success';
};

const formatMs = ms => (ms == null ? '–' : `${ms} ms`);

// Shorten a hostname for table display while keeping enough context
// to distinguish between machines in a cluster.
const shortHost = h => {
  if (!h) return '–';
  // ip-172-31-23-144 → ip-23-144
  const ipMatch = h.match(/^ip-\d+-\d+-(\d+-\d+)$/);
  if (ipMatch) return `ip-${ipMatch[1]}`;
  // generic: keep first segment if dotted
  if (h.includes('.')) return h.split('.')[0];
  return h.length > 14 ? `${h.slice(0, 12)}…` : h;
};

const ClusterHealth = () => {
  const authCtx = useContext(AuthContext);
  const {
    clusterStatus,
    leaderLog,
    cronStatus,
    isLoading,
    fetchClusterStatus,
    fetchLeaderLog,
    fetchCronStatus,
    resetMongoStats,
  } = useClusterHealth();
  const [lastRefreshed, setLastRefreshed] = useState(null);
  // Mongo stats come from the per-process heartbeat snapshots embedded in
  // clusterStatus.processes[i].mongoOps — no separate fetch needed. The
  // toggle controls visibility (the section is hidden by default to keep
  // the dashboard clean) and Reset broadcasts a cluster-wide clear.
  const [mongoStatsVisible, setMongoStatsVisible] = useState(false);
  const [mongoStatsResetting, setMongoStatsResetting] = useState(false);

  // Direct URL access guard — needsRights on the menu only hides the entry,
  // it does not block navigation. Without this, users without cluster:read
  // would render the page and only see 401/403 toasts on every refresh.
  const hasClusterRead = authCtx.checkRight('cluster:read');

  const refresh = useCallback(async () => {
    if (!hasClusterRead) return;
    await Promise.all([fetchClusterStatus(), fetchLeaderLog(), fetchCronStatus()]);
    setLastRefreshed(new Date());
  }, [hasClusterRead, fetchClusterStatus, fetchLeaderLog, fetchCronStatus]);

  useEffect(() => {
    if (!hasClusterRead) return undefined;
    refresh();
    const interval = setInterval(refresh, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [hasClusterRead, refresh]);

  const handleResetMongoStats = useCallback(async () => {
    setMongoStatsResetting(true);
    try {
      await resetMongoStats();
      // Pull fresh cluster status — the next /cluster-health response should
      // show empty mongoOps[] arrays once each instance's publisher cycle
      // (~30s) has run. We refresh immediately for a quick "we did something"
      // signal even though the actual data may still show stale entries
      // until the next publisher tick.
      await fetchClusterStatus();
    } finally {
      setMongoStatsResetting(false);
    }
  }, [resetMongoStats, fetchClusterStatus]);

  if (!hasClusterRead) {
    return <Navigate to='/welcome' replace />;
  }

  const formatUptime = seconds => {
    if (seconds == null) return '–';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return m ? `${h}h${m}m` : `${h}h`;
  };

  const formatRelative = iso => {
    if (!iso) return '–';
    const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m`;
    return `${Math.floor(sec / 3600)}h`;
  };

  const formatRelativeFuture = iso => {
    if (!iso) return '–';
    const sec = Math.floor((new Date(iso).getTime() - Date.now()) / 1000);
    if (sec <= 0) return 'jetzt';
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
    return `${Math.floor(sec / 86400)}d`;
  };

  const formatTimestamp = iso => {
    if (!iso) return '–';
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDuration = ms => {
    if (ms == null) return '–';
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  // Sort: hostname asc, then PM2 instance asc — keeps machines grouped together.
  const sortedProcesses = useMemo(() => {
    const procs = clusterStatus?.processes || [];
    return [...procs].sort((a, b) => {
      const h = (a.hostname || '').localeCompare(b.hostname || '');
      if (h !== 0) return h;
      return Number(a.pm2Instance || 0) - Number(b.pm2Instance || 0);
    });
  }, [clusterStatus]);

  // Aggregate metrics for the at-a-glance row.
  // For p99 metrics we take MAX across instances (worst-case visibility) —
  // taking mean would let one slow machine hide behind healthy ones.
  const aggregate = useMemo(() => {
    const procs = sortedProcesses;
    if (!procs.length) return null;

    const maxOf = (key, sub) => {
      const vals = procs.map(p => p?.[key]?.[sub]).filter(v => v != null);
      return vals.length ? Math.max(...vals) : null;
    };
    const meanOf = (key, sub) => {
      const vals = procs.map(p => p?.[key]?.[sub]).filter(v => v != null);
      if (!vals.length) return null;
      return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100;
    };

    const eventLoopP99Max = maxOf('eventLoop', 'p99');
    const mongoPingMean = meanOf('mongoLatency', 'mean');
    const mongoQueryP99Max = maxOf('mongoQueryLatency', 'p99');
    const mongoQuerySamples = procs.reduce(
      (sum, p) => sum + (p?.mongoQueryLatency?.samples || 0),
      0
    );
    const redisMean = meanOf('redisLatency', 'mean');

    const totalSocketClients = procs.reduce(
      (sum, p) => sum + (p?.socket?.connected || 0),
      0
    );

    const leader = procs.find(p => p.isLeader);
    const mqttPublisher = leader?.mqtt?.publisherConnected ?? false;
    const mqttSubscriber = leader?.mqtt?.subscriberConnected ?? false;

    const queues = clusterStatus?.queues?.queues || {};
    const totalPending = Object.values(queues).reduce(
      (sum, q) => sum + (q?.pending || 0),
      0
    );
    const worstQueue = Object.entries(queues)
      .filter(([, v]) => v?.pending > 0)
      .sort((a, b) => (b[1].pending || 0) - (a[1].pending || 0))[0];

    const machineCount = new Set(procs.map(p => p.hostname)).size;

    return {
      eventLoopP99Max,
      mongoPingMean,
      mongoQueryP99Max,
      mongoQuerySamples,
      redisMean,
      totalSocketClients,
      mqttPublisher,
      mqttSubscriber,
      mqttLeaderHost: leader?.hostname,
      totalPending,
      worstQueue,
      machineCount,
      processCount: procs.length,
      queueSnapshotAt: clusterStatus?.queues?.timestamp,
    };
  }, [sortedProcesses, clusterStatus]);

  const cronAlertCount = useMemo(
    () =>
      (cronStatus || []).filter(c => c.isOverdue || c.isStuck || c.lastStatus === 'error')
        .length,
    [cronStatus]
  );

  // Compact cell sx — minimize horizontal padding.
  const compactCell = { px: 1, py: 0.75 };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Cluster Health</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          {lastRefreshed && (
            <Typography variant="caption" color="text.secondary">
              Aktualisiert: {formatTimestamp(lastRefreshed.toISOString())}
            </Typography>
          )}
          {aggregate && (
            <Typography variant="caption" color="text.secondary">
              · {aggregate.processCount} Worker auf {aggregate.machineCount} Maschine
              {aggregate.machineCount === 1 ? '' : 'n'}
            </Typography>
          )}
          <Tooltip title="Aktualisieren">
            <IconButton onClick={refresh} disabled={isLoading} size="small">
              {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Connection chips */}
      {clusterStatus && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
          <Chip
            label={`Redis: ${clusterStatus.redis}`}
            color={clusterStatus.redis === 'connected' ? 'success' : 'error'}
            size="small"
          />
          <Chip
            label={`Mongo: ${clusterStatus.mongo}`}
            color={clusterStatus.mongo === 'connected' ? 'success' : 'error'}
            size="small"
          />
          <Tooltip title={clusterStatus.currentLeader || 'kein Leader'}>
            <Chip
              label={`Leader: ${shortHost(clusterStatus.currentLeader?.split(':')[0]) || 'kein'}`}
              color={clusterStatus.currentLeader ? 'primary' : 'warning'}
              size="small"
              icon={<StarIcon />}
            />
          </Tooltip>
        </Stack>
      )}

      {/* Health at a glance */}
      {aggregate && (
        <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
          <Chip
            label={`EL p99: ${formatMs(aggregate.eventLoopP99Max)}`}
            color={colorForLatency(
              aggregate.eventLoopP99Max,
              THRESHOLDS.eventLoopP99WarnMs,
              THRESHOLDS.eventLoopP99ErrorMs
            )}
            size="small"
          />
          <Chip
            label={`Mongo Ping Ø: ${formatMs(aggregate.mongoPingMean)}`}
            color={colorForLatency(
              aggregate.mongoPingMean,
              THRESHOLDS.mongoMeanWarnMs,
              THRESHOLDS.mongoMeanErrorMs
            )}
            size="small"
          />
          {aggregate.mongoQuerySamples > 0 && (
            <Tooltip title={`Worst-case p99 über alle Worker (${aggregate.mongoQuerySamples} Samples insgesamt)`}>
              <Chip
                label={`Mongo Query p99: ${formatMs(aggregate.mongoQueryP99Max)}`}
                color={colorForLatency(
                  aggregate.mongoQueryP99Max,
                  THRESHOLDS.mongoQueryP99WarnMs,
                  THRESHOLDS.mongoQueryP99ErrorMs
                )}
                size="small"
              />
            </Tooltip>
          )}
          <Chip
            label={`Redis Ø: ${formatMs(aggregate.redisMean)}`}
            color={colorForLatency(
              aggregate.redisMean,
              THRESHOLDS.redisMeanWarnMs,
              THRESHOLDS.redisMeanErrorMs
            )}
            size="small"
          />
          <Chip
            label={`Queues: ${aggregate.totalPending}${
              aggregate.worstQueue ? ` (${aggregate.worstQueue[0]})` : ''
            }`}
            color={colorForCount(
              aggregate.totalPending,
              THRESHOLDS.totalPendingWarn,
              THRESHOLDS.totalPendingError
            )}
            size="small"
          />
          <Chip
            label={`Sockets: ${aggregate.totalSocketClients}`}
            size="small"
          />
          <Tooltip
            title={`MQTT-Status vom Leader (${shortHost(aggregate.mqttLeaderHost) || '?'})`}
          >
            <Chip
              label={`MQTT: ${aggregate.mqttPublisher ? 'pub✓' : 'pub✗'} ${aggregate.mqttSubscriber ? 'sub✓' : 'sub✗'}`}
              color={aggregate.mqttPublisher && aggregate.mqttSubscriber ? 'success' : 'error'}
              size="small"
            />
          </Tooltip>
          {cronAlertCount > 0 && (
            <Chip
              label={`Crons: ${cronAlertCount}`}
              color="error"
              size="small"
              icon={<WarningAmberIcon />}
            />
          )}
        </Stack>
      )}

      {/* Process table */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Prozesse
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={compactCell}>#</TableCell>
              <TableCell sx={compactCell}>Status</TableCell>
              <TableCell sx={compactCell}>Worker</TableCell>
              <TableCell sx={compactCell} align="right">Up</TableCell>
              <TableCell sx={compactCell} align="right">RSS</TableCell>
              <TableCell sx={compactCell} align="right">EL p99</TableCell>
              <TableCell sx={compactCell} align="right">Mongo</TableCell>
              <TableCell sx={compactCell} align="right">Redis Ø</TableCell>
              <TableCell sx={compactCell} align="right">Sock</TableCell>
              <TableCell sx={compactCell} align="right">HB</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedProcesses.length > 0 ? (
              sortedProcesses.map((proc, idx) => {
                const isLeader = proc.instanceId === clusterStatus.currentLeader;
                const heartbeatAge = proc.timestamp
                  ? (Date.now() - new Date(proc.timestamp).getTime()) / 1000
                  : 999;
                const isAlive = heartbeatAge < 25;
                const elP99 = proc.eventLoop?.p99;
                const mongoPing = proc.mongoLatency?.mean;
                const mongoQueryP99 = proc.mongoQueryLatency?.p99;
                const mongoSamples = proc.mongoQueryLatency?.samples || 0;
                const redisMean = proc.redisLatency?.mean;

                // One Mongo column: prefer query p99 (real workload signal),
                // fall back to ping mean (network/liveness only) when monitoring is off.
                const showQuery = mongoSamples > 0;
                const mongoValue = showQuery ? mongoQueryP99 : mongoPing;
                const mongoColor = colorForLatency(
                  mongoValue,
                  showQuery ? THRESHOLDS.mongoQueryP99WarnMs : THRESHOLDS.mongoMeanWarnMs,
                  showQuery ? THRESHOLDS.mongoQueryP99ErrorMs : THRESHOLDS.mongoMeanErrorMs
                );
                const mongoTooltip = showQuery
                  ? `Query p99 (mean ${formatMs(proc.mongoQueryLatency?.mean)}, ${mongoSamples} samples)`
                  : `Ping Ø (Query-Monitoring inaktiv)`;
                const mongoLabel = showQuery
                  ? `q ${formatMs(mongoValue)}`
                  : `p ${formatMs(mongoValue)}`;

                return (
                  <TableRow
                    key={proc.instanceId}
                    sx={isLeader ? { backgroundColor: 'action.selected' } : {}}
                  >
                    <TableCell sx={compactCell}>
                      <Typography variant="caption" color="text.secondary">
                        {idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={compactCell}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Tooltip title={isAlive ? `online (${Math.round(heartbeatAge)}s)` : 'kein Heartbeat'}>
                          <FiberManualRecordIcon
                            sx={{ fontSize: 12, color: isAlive ? 'success.main' : 'error.main' }}
                          />
                        </Tooltip>
                        {isLeader ? (
                          <Tooltip title="Leader">
                            <StarIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Follower">
                            <Typography variant="caption" sx={{ fontWeight: 'bold', width: 14, textAlign: 'center' }}>
                              F
                            </Typography>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={compactCell}>
                      <Tooltip title={`${proc.hostname} · pid ${proc.pid}`}>
                        <Typography variant="body2" fontFamily="monospace">
                          {shortHost(proc.hostname)} #{proc.pm2Instance}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={compactCell} align="right">
                      <Typography variant="caption">{formatUptime(proc.uptime)}</Typography>
                    </TableCell>
                    <TableCell sx={compactCell} align="right">
                      <Tooltip
                        title={
                          proc.heapUsedMB != null
                            ? `RSS ${proc.memoryMB} MB · Heap ${proc.heapUsedMB} MB`
                            : `RSS ${proc.memoryMB} MB`
                        }
                      >
                        <Typography variant="caption">{proc.memoryMB}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={compactCell} align="right">
                      <Tooltip
                        title={`mean ${formatMs(proc.eventLoop?.mean)} · max ${formatMs(proc.eventLoop?.max)}`}
                      >
                        <Chip
                          label={formatMs(elP99)}
                          size="small"
                          color={colorForLatency(
                            elP99,
                            THRESHOLDS.eventLoopP99WarnMs,
                            THRESHOLDS.eventLoopP99ErrorMs
                          )}
                          variant="outlined"
                          sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={compactCell} align="right">
                      <Tooltip title={mongoTooltip}>
                        <Chip
                          label={mongoLabel}
                          size="small"
                          color={mongoColor}
                          variant="outlined"
                          sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={compactCell} align="right">
                      <Tooltip
                        title={`p99 ${formatMs(proc.redisLatency?.p99)} · ${proc.redisLatency?.samples ?? 0} samples`}
                      >
                        <Chip
                          label={formatMs(redisMean)}
                          size="small"
                          color={colorForLatency(
                            redisMean,
                            THRESHOLDS.redisMeanWarnMs,
                            THRESHOLDS.redisMeanErrorMs
                          )}
                          variant="outlined"
                          sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={compactCell} align="right">
                      <Typography variant="caption">{proc.socket?.connected ?? '–'}</Typography>
                    </TableCell>
                    <TableCell sx={compactCell} align="right">
                      <Tooltip title={formatTimestamp(proc.timestamp)}>
                        <Typography variant="caption" color="text.secondary">
                          {formatRelative(proc.timestamp)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={compactCell}>
                  {isLoading ? <CircularProgress size={20} /> : 'Keine Prozesse gefunden'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Queue table */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        BullMQ Queues
        {clusterStatus?.queues?.timestamp && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            Snapshot: {formatTimestamp(clusterStatus.queues.timestamp)}
          </Typography>
        )}
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={compactCell}>#</TableCell>
              <TableCell sx={compactCell}>Queue</TableCell>
              <TableCell sx={compactCell} align="right">Pending</TableCell>
              <TableCell sx={compactCell} align="right">Wait</TableCell>
              <TableCell sx={compactCell} align="right">Active</TableCell>
              <TableCell sx={compactCell} align="right">Delayed</TableCell>
              <TableCell sx={compactCell} align="right">Failed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clusterStatus?.queues?.queues ? (
              Object.entries(clusterStatus.queues.queues)
                .sort((a, b) => (b[1].pending || 0) - (a[1].pending || 0))
                .map(([name, q], idx) => (
                  <TableRow
                    key={name}
                    sx={q.pending > 0 ? { backgroundColor: 'action.hover' } : {}}
                  >
                    <TableCell sx={compactCell}>
                      <Typography variant="caption" color="text.secondary">
                        {idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={compactCell}>
                      <Typography variant="body2" fontFamily="monospace">
                        {name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={compactCell} align="right">
                      <Chip
                        size="small"
                        label={q.pending ?? 0}
                        color={
                          q.pending >= THRESHOLDS.totalPendingError
                            ? 'error'
                            : q.pending >= THRESHOLDS.totalPendingWarn
                              ? 'warning'
                              : q.pending > 0
                                ? 'default'
                                : 'success'
                        }
                        variant="outlined"
                        sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
                      />
                    </TableCell>
                    <TableCell sx={compactCell} align="right">{q.waiting ?? 0}</TableCell>
                    <TableCell sx={compactCell} align="right">{q.active ?? 0}</TableCell>
                    <TableCell sx={compactCell} align="right">{q.delayed ?? 0}</TableCell>
                    <TableCell sx={compactCell} align="right">{q.failed ?? 0}</TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={compactCell}>
                  Noch kein Queue-Snapshot — wird vom Leader alle 30s erstellt.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Cron jobs */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Cron Jobs
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={compactCell}>#</TableCell>
              <TableCell sx={compactCell}>Status</TableCell>
              <TableCell sx={compactCell}>Name</TableCell>
              <TableCell sx={compactCell}>Cron</TableCell>
              <TableCell sx={compactCell}>Letzter Lauf</TableCell>
              <TableCell sx={compactCell} align="right">Dauer</TableCell>
              <TableCell sx={compactCell}>Nächster Lauf</TableCell>
              <TableCell sx={compactCell}>Fehler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cronStatus?.length > 0 ? (
              cronStatus.map((c, idx) => {
                let statusChip;
                if (c.isStuck) {
                  statusChip = <Chip size="small" color="error" label="hängt" />;
                } else if (c.isCurrentlyRunning) {
                  statusChip = <Chip size="small" color="info" label="läuft" />;
                } else if (c.isOverdue) {
                  statusChip = (
                    <Tooltip title={`überfällig seit ~${formatDuration(c.overdueByMs)}`}>
                      <Chip size="small" color="warning" label="overdue" />
                    </Tooltip>
                  );
                } else if (c.lastStatus === 'error') {
                  statusChip = <Chip size="small" color="error" label="Fehler" />;
                } else if (c.lastStatus === 'success') {
                  statusChip = <Chip size="small" color="success" label="OK" />;
                } else {
                  statusChip = <Chip size="small" label="–" variant="outlined" />;
                }
                return (
                  <TableRow key={c.name}>
                    <TableCell sx={compactCell}>
                      <Typography variant="caption" color="text.secondary">
                        {idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={compactCell}>{statusChip}</TableCell>
                    <TableCell sx={compactCell}>
                      <Typography variant="body2" fontFamily="monospace">
                        {c.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={compactCell}>
                      <Typography variant="caption" fontFamily="monospace">
                        {c.cronExpression || '–'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={compactCell}>
                      <Tooltip title={formatTimestamp(c.lastFinishedAt || c.lastStartedAt)}>
                        <Typography variant="caption">
                          vor {formatRelative(c.lastFinishedAt || c.lastStartedAt)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={compactCell} align="right">
                      <Typography variant="caption">{formatDuration(c.lastDurationMs)}</Typography>
                    </TableCell>
                    <TableCell sx={compactCell}>
                      {c.nextScheduledAt ? (
                        <Tooltip title={formatTimestamp(c.nextScheduledAt)}>
                          <Typography variant="caption">
                            in {formatRelativeFuture(c.nextScheduledAt)}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">–</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={compactCell}>
                      {c.lastError ? (
                        <Tooltip title={c.lastError}>
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{
                              maxWidth: 200,
                              display: 'inline-block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              verticalAlign: 'middle',
                            }}
                          >
                            {c.lastError}
                          </Typography>
                        </Tooltip>
                      ) : (
                        '–'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={compactCell}>
                  Noch keine Cron-Läufe protokolliert.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mongo per-(command, collection) stats — sourced from each instance's
          dedicated cluster:mongo-ops:<id> Redis key, merged into
          clusterStatus.processes by the backend. Section is collapsed by
          default (diagnostic, not steady-state).  */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <StorageIcon fontSize="small" />
          <Typography variant="h6">Mongo Query Stats</Typography>
          <Typography variant="caption" color="text.secondary">
            pro Instanz, ~30s aktuell
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setMongoStatsVisible(v => !v)}
          >
            {mongoStatsVisible ? 'Ausblenden' : 'Anzeigen'}
          </Button>
          {mongoStatsVisible && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={handleResetMongoStats}
              disabled={mongoStatsResetting}
            >
              {mongoStatsResetting ? 'Zurücksetzen…' : 'Cluster-weit zurücksetzen'}
            </Button>
          )}
        </Stack>
      </Stack>

      {mongoStatsVisible && (
        <>
          {sortedProcesses.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Noch keine Process-Daten geladen.
            </Alert>
          )}

          {sortedProcesses.length > 0 &&
            sortedProcesses.every(p => !p.mongoOps) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Keine Mongo-Stats von einer Instanz vorhanden. Möglicherweise ist{' '}
                <code>MONGO_COMMAND_MONITORING</code> auf den Backend-Instanzen
                nicht aktiviert oder der Publisher-Cycle (~30s) ist noch nicht
                durch.
              </Alert>
            )}

          {sortedProcesses.map(proc => {
            const ops = proc.mongoOps || [];
            const slowThreshold = proc.mongoSlowThresholdMs || 200;
            return (
              <Box key={proc.instanceId} sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                    {proc.instanceId}
                  </Typography>
                  {proc.isLeader && (
                    <Tooltip title="Leader">
                      <StarIcon fontSize="inherit" color="warning" />
                    </Tooltip>
                  )}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Slow ≥ ${slowThreshold} ms`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${ops.length} Operationen`}
                  />
                  {proc.mongoOpsAt && (
                    <Tooltip title={formatTimestamp(proc.mongoOpsAt)}>
                      <Typography variant="caption" color="text.secondary">
                        Snapshot {formatRelative(proc.mongoOpsAt)}
                      </Typography>
                    </Tooltip>
                  )}
                </Stack>
                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={compactCell}>cmd</TableCell>
                        <TableCell sx={compactCell}>collection</TableCell>
                        <TableCell align="right" sx={compactCell}>
                          Calls
                        </TableCell>
                        <TableCell align="right" sx={compactCell}>
                          Slow
                        </TableCell>
                        <TableCell align="right" sx={compactCell}>
                          Failed
                        </TableCell>
                        <TableCell align="right" sx={compactCell}>
                          mean
                        </TableCell>
                        <TableCell align="right" sx={compactCell}>
                          p50
                        </TableCell>
                        <TableCell align="right" sx={compactCell}>
                          p99
                        </TableCell>
                        <TableCell align="right" sx={compactCell}>
                          max
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ops.length > 0 ? (
                        ops.map(op => {
                          const p99Color =
                            op.p99Ms >= 500
                              ? 'error'
                              : op.p99Ms >= slowThreshold
                              ? 'warning'
                              : 'success';
                          return (
                            <TableRow key={`${op.cmd}:${op.coll}`}>
                              <TableCell sx={{ ...compactCell, fontFamily: 'monospace' }}>
                                {op.cmd}
                              </TableCell>
                              <TableCell sx={{ ...compactCell, fontFamily: 'monospace' }}>
                                {op.coll}
                              </TableCell>
                              <TableCell align="right" sx={compactCell}>
                                {op.count?.toLocaleString('de-DE') ?? '–'}
                              </TableCell>
                              <TableCell align="right" sx={compactCell}>
                                {op.slowCount > 0 ? (
                                  <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label={op.slowCount}
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    0
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={compactCell}>
                                {op.failedCount > 0 ? (
                                  <Chip
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                    label={op.failedCount}
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    0
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right" sx={compactCell}>
                                {formatMs(op.meanMs)}
                              </TableCell>
                              <TableCell align="right" sx={compactCell}>
                                {formatMs(op.p50Ms)}
                              </TableCell>
                              <TableCell align="right" sx={compactCell}>
                                <Chip size="small" color={p99Color} label={formatMs(op.p99Ms)} />
                              </TableCell>
                              <TableCell align="right" sx={compactCell}>
                                {formatMs(op.maxMs)}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={compactCell}>
                            {proc.mongoOps
                              ? 'Keine Operationen seit letztem Reset erfasst.'
                              : 'Noch kein Snapshot verfügbar (Publisher läuft alle 30 s).'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}
        </>
      )}

      {/* Leader change log */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Leader-Wechsel
      </Typography>
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={compactCell}>#</TableCell>
              <TableCell sx={compactCell}>Zeitpunkt</TableCell>
              <TableCell sx={compactCell}>Event</TableCell>
              <TableCell sx={compactCell}>Instanz</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaderLog?.length > 0 ? (
              leaderLog.map((entry, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={compactCell}>
                    <Typography variant="caption" color="text.secondary">
                      {idx + 1}
                    </Typography>
                  </TableCell>
                  <TableCell sx={compactCell}>
                    <Tooltip title={formatTimestamp(entry.timestamp)}>
                      <Typography variant="caption">
                        vor {formatRelative(entry.timestamp)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={compactCell}>
                    <Chip
                      label={entry.event === 'acquired' ? 'übernommen' : 'abgegeben'}
                      color={entry.event === 'acquired' ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={compactCell}>
                    <Tooltip title={entry.instanceId}>
                      <Typography variant="caption" fontFamily="monospace">
                        {shortHost(entry.instanceId?.split(':')[0])}
                        {' '}
                        #{entry.instanceId?.split(':')[2]}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={compactCell}>
                  Keine Leader-Wechsel protokolliert
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!clusterStatus && !isLoading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Cluster-Health-Daten werden geladen...
        </Alert>
      )}
    </Box>
  );
};

export default ClusterHealth;
